"use client"

import React from 'react';
import { useBranchCode } from '@/components/SiteHostProvider';
import type { CatalogueScopeState } from '@/lib/catalog/catalogue-scope';
import { getReviewedDocumentListHtml } from '@/lib/catalog/document-list-copy';
import { usePathname } from 'next/navigation';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getBranchData, getCurrentBranch } from '@/lib/config/branches-config';

interface ServiceIncludesProps {
  catalogueScopeState?: CatalogueScopeState;
  className?: string;
  includeData?: {
    title?: string;
    description?: string;
  } | null;
  docsData?: {
    title?: string;
    description?: string;
    items?: Array<{
      title?: string;
      description?: string;
    }>;
  } | null;
  techDocs?: Array<{
    src: string;
    name: string;
  }> | null;
}

export default function ServiceIncludes({ catalogueScopeState = 'unknown', className, includeData, docsData, techDocs }: ServiceIncludesProps) {
  const pathname = usePathname() || '/';
  // Филиал страницы: по хосту (BRANCH_ROUTING=host) или по /geo/<code>/.
  const pageBranchCode = useBranchCode();
  const branchCode = pageBranchCode;
  const isMoscow = branchCode === 'msk';
  // Телефон был захардкожен в обеих ветках и расходился с branches-config.
  // Для локального SEO NAP обязан совпадать с тем, что уходит в LocalBusiness.
  const branch = getBranchData(branchCode) || getCurrentBranch();
  const branchPhone = branch?.phone || '';
  const branchPhoneHref = branchPhone.replace(/[^+\d]/g, '');
  
  const hasIncludes = Boolean(includeData?.description);
  const docsItems = Array.isArray(docsData?.items) ? docsData?.items : [];
  const hasDocs = docsItems.length > 0 && docsItems.some((x) => Boolean(x?.description));
  const techDocsItems = Array.isArray(techDocs) ? techDocs : [];
  const hasTechDocs = techDocsItems.length > 0 && techDocsItems.some((x) => Boolean(x?.src) && Boolean(x?.name));

  // Если нет данных, не показываем компонент
  if (!hasIncludes && !hasDocs && !hasTechDocs) {
    return null;
  }

  const defaultTab = hasIncludes ? 'includes' : hasDocs ? 'docs' : hasTechDocs ? 'tech-docs' : 'guarantee';
  
  return (
    <section className={`py-4 sm:py-8 ${className || ''}`}>
      <div className="w-full">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList
            className="w-full flex flex-nowrap items-center justify-start gap-1 sm:gap-2 h-auto p-1 sm:p-1.5 bg-[#0d223c] text-white underline outline-none dark:bg-[#0d223c] dark:text-white overflow-x-auto scrollbar-hide"
          >
            {hasIncludes && (
              <TabsTrigger
                value="includes"
                className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
              >
                {includeData?.title || 'Что входит в услугу'}
              </TabsTrigger>
            )}
            {hasDocs && (
              <TabsTrigger
                value="docs"
                className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
              >
                Документы
              </TabsTrigger>
            )}
            {hasTechDocs && (
              <TabsTrigger
                value="tech-docs"
                className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
              >
                Техническая документация
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="guarantee" 
              className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
            >
              Гарантии
            </TabsTrigger>
            <TabsTrigger 
              value="delivery" 
              className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
            >
              Доставка
            </TabsTrigger>
            <TabsTrigger 
              value="payment" 
              className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
            >
              Оплата
            </TabsTrigger>
          </TabsList>

          {hasIncludes && (
            <TabsContent value="includes" className="mt-4 sm:mt-6">
              <div
                className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm"
                dangerouslySetInnerHTML={{ __html: includeData?.description || '' }}
              />
            </TabsContent>
          )}

          {hasDocs && (
            <TabsContent value="docs" className="mt-4 sm:mt-6">
              <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <div className="[&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4 [&_li]:mt-1">
                  {docsItems
                    .filter((x) => Boolean(x?.description))
                    .map((item, idx) => (
                      <div
                        key={`${item?.title || 'item'}-${idx}`}
                        className={idx === 0 ? '' : 'mt-6 pt-6 border-t border-slate-200'}
                        dangerouslySetInnerHTML={{ __html: getReviewedDocumentListHtml(item?.description || '', catalogueScopeState) }}
                      />
                    ))}
                </div>
              </div>
            </TabsContent>
          )}

          {hasTechDocs && (
            <TabsContent value="tech-docs" className="mt-4 sm:mt-6">
              <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <ul className="space-y-2">
                  {techDocsItems
                    .filter((x) => Boolean(x?.src) && Boolean(x?.name))
                    .map((doc) => (
                      <li key={doc.src}>
                        <a
                          href={doc.src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                        >
                          {doc.name}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            </TabsContent>
          )}

          <TabsContent value="guarantee" className="mt-4 sm:mt-6">
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Условия гарантии на изделие и порядок приёмки указываются в документации конкретного заказа.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Требования к воротам, комплектности и монтажу согласуются в спецификации и договоре.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Условия и срок гарантии на металлокаркас фиксируются в договоре для конкретного проекта.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Требования к эксплуатации, обслуживанию и защитному покрытию согласуются в документации заказа. Законные права покупателя сохраняются.</span>
                </li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="delivery" className="mt-4 sm:mt-6">
            <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 text-sm text-gray-700 shadow-sm sm:p-6 sm:text-base">
              <h3 className="font-semibold text-gray-900">Доставка и самовывоз</h3>
              <p>Организуем доставку {isMoscow ? 'по Москве и Московской области' : 'по Санкт-Петербургу и Ленинградской области'} и в другие регионы России. Самовывоз возможен по предварительному согласованию.</p>
              <p>Стоимость, транспорт, дата и место передачи зависят от размеров и массы изделий, маршрута и условий разгрузки. Эти условия фиксируются в расчёте или договоре. Срок изготовления и срок перевозки согласуются отдельно.</p>
              <p>До оплаты уточните погрузку, разгрузку, подъезд к объекту, необходимую технику и документы получателя.</p>
              <p>Запросить расчёт доставки: <a href={`tel:${branchPhoneHref}`} className="font-medium text-blue-600 hover:text-blue-800">{branchPhone}</a>.</p>
              <a href="/pages/dostavka/" className="inline-flex min-h-11 items-center text-blue-600 underline">Полные условия доставки</a>
            </div>
          </TabsContent>

          <TabsContent value="payment" className="mt-4 sm:mt-6">
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                Оплата согласованного заказа
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 font-semibold text-blue-700 text-xs sm:text-sm">
                    1
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Размер предоплаты, этапы оплаты и срок окончательного расчёта фиксируются для конкретного заказа до оплаты.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 font-semibold text-blue-700 text-xs sm:text-sm">
                    2
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Для организаций предусмотрен безналичный расчёт. Доступный способ оплаты частного заказа, возможность отсрочки или факторинга уточняются у менеджера до заключения договора.</span>
                </li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
